import { Badge, Button, Card, Col, Form, Input, message, Row, Space, Tabs, TabsProps } from "antd";
import React, { useEffect, useState } from "react";
import HeaderSection from "../../components/HeaderSection";
import { MailOutlined } from "@ant-design/icons";
import { generateFormRules } from "../../helpers/formRules";
import { httpRequest } from "../../helpers/api";
import { BaseResponseProps } from "../../types/config.type";
import { getErrorMessage, showErrorMessage } from "../../helpers/errorHandler";
import { errorModal, successModal } from "../../helpers/modal";
import Listing from "./Listing";
import Agent from "./Agent";
import { EmployeeProperties } from "../../types/employee.type";
import useFetchList from "../../hooks/useFetchList";
import { ListingProperties, ListListingQuery } from "../../types/listing.type";
import NotSet from "../../components/NotSet";
import { LAMUDI_ID, R123_PORTAL_ID } from "../employee/constant";

interface R123AgentObject {
	r123agent_id: number;
	firstname: string;
	lastname?: string;
	username?: string;
	status?: "A" | string; // Unknown yet
	email: string;
	profile?: string;
	company_id: number;
	company?: string;
	address?: string;
	photo: string;
	phone?: string;
	mobile: string;
	whatsapp?: string;
	updated_time?: string; // YYYY-MM-DD HH:mm:ss
}

const PortalChecker: React.FC = () => {
	const [form] = Form.useForm<{ email: string }>();
	const [isLoading, setIsLoading] = useState(false);
	const [activeTab, setActiveTab] = useState<string>("listing");
	const [activeTabPage, setActiveTabPage] = useState<string>("rumah123");
	const [email, setEmail] = useState<string>("");
	const [isChecker, setIsChecker] = useState<boolean>(false);

	const listingFetch = useFetchList<
		ListingProperties,
		ListListingQuery & { filterEmail: string; filterPortalId: string }
	>({
		endpoint: "listings/portal/list",
		initialQuery: {
			sort: "createdAt:DESC",
			filterEmail: email,
			filterPortalId: activeTabPage === "rumah123" ? R123_PORTAL_ID : LAMUDI_ID,
		},
		fetchable: !!email,
	});

	const agentFetch = useFetchList<EmployeeProperties, any>({
		endpoint: "employees/portal/list",
		initialQuery: {
			sort: "createdAt:DESC",
			includeData: "user,office",
			filterEmail: email,
			filterPortalId: activeTabPage === "rumah123" ? R123_PORTAL_ID : LAMUDI_ID,
		},
		fetchable: !!email,
	});

	const emailWatch = Form.useWatch("email", form);

	const handleGetRumah123Agent = async (email: string) => {
		try {
			const res = await httpRequest.post<
				BaseResponseProps<{
					isFound: boolean;
					message: string;
					email: string;
					agent: R123AgentObject;
				}>
			>(`/rumah123/get-agent`, {
				email: email,
			});
			listingFetch.setQuery({
				...listingFetch.query,
				filterEmail: email,
				filterPortalId: R123_PORTAL_ID,
			});

			agentFetch.setQuery({
				...agentFetch.query,
				filterEmail: email,
				filterPortalId: R123_PORTAL_ID,
			});
			setEmail(email);
			setIsChecker(true);

			if (res.data.payload.isFound) {
				const agent = res.data.payload.agent;

				successModal({
					title: "Email Found",
					subtitle: (
						<Space
							direction="vertical"
							style={{ textAlign: "left" }}
						>
							<Row>
								<Col span={8}>
									<strong>Rumah123 Agent ID</strong>
								</Col>
								<Col span={14}>: {agent.r123agent_id}</Col>
							</Row>
							<Row>
								<Col span={8}>
									<strong>Firstname</strong>
								</Col>
								<Col span={14}>: {agent.firstname}</Col>
							</Row>
							<Row>
								<Col span={8}>
									<strong>Lastname</strong>
								</Col>
								<Col span={14}>: {agent.lastname}</Col>
							</Row>
							<Row>
								<Col span={8}>
									<strong>Username</strong>
								</Col>
								<Col span={14}>: {agent.username}</Col>
							</Row>
							<Row>
								<Col span={8}>
									<strong>Email</strong>
								</Col>
								<Col span={14}>: {agent.email}</Col>
							</Row>
							<Row>
								<Col span={8}>
									<strong>Company ID</strong>
								</Col>
								<Col span={14}>: {agent.company_id}</Col>
							</Row>
							<Row>
								<Col span={8}>
									<strong>Company</strong>
								</Col>
								<Col span={14}>: {agent.company}</Col>
							</Row>
							<Row>
								<Col span={8}>
									<strong>Profile</strong>
								</Col>
								<Col span={14}>: {agent.profile}</Col>
							</Row>
							<Row>
								<Col span={8}>
									<strong>Address</strong>
								</Col>
								<Col span={14}>: {agent.address}</Col>
							</Row>
							<Row>
								<Col span={8}>
									<strong>Status</strong>
								</Col>
								<Col span={14}>: {agent.status}</Col>
							</Row>
						</Space>
					),
				});
			} else {
				errorModal({
					title: "Email Not Found",
					subtitle: res.data.payload.message,
				});
			}
		} catch (error) {
			showErrorMessage(error, true);
		}
	};

	const handleGetLamudiAgent = async (email: string) => {
		try {
			const res = await httpRequest.get<
				BaseResponseProps<{
					email: string;
					externalId: string;
					name: string;
					phone: string;
				}>
			>(`/lamudi/ID/publishers/${email}`);
			listingFetch.setQuery({
				...listingFetch.query,
				filterEmail: email,
				filterPortalId: LAMUDI_ID,
			});

			agentFetch.setQuery({
				...agentFetch.query,
				filterEmail: email,
				filterPortalId: LAMUDI_ID,
			});
			setEmail(email);
			setIsChecker(true);
			if (res.data.payload.externalId) {
				const agent = res.data.payload;

				successModal({
					title: "Email Found",
					subtitle: (
						<Space
							direction="vertical"
							style={{ textAlign: "left" }}
						>
							<Row>
								<Col span={8}>
									<strong>Email</strong>
								</Col>
								<Col span={14}>: {agent.externalId}</Col>
							</Row>
							<Row>
								<Col span={8}>
									<strong>Name</strong>
								</Col>
								<Col span={14}>: {agent.name}</Col>
							</Row>
							<Row>
								<Col span={8}>
									<strong>Phone</strong>
								</Col>
								<Col span={14}>: {agent.phone || <NotSet />}</Col>
							</Row>
						</Space>
					),
				});
			} else {
				errorModal({
					title: "Email not found",
				});
			}
		} catch (error) {
			errorModal({
				title: "Email not found",
			});
		}
	};

	const handleChecker = async () => {
		const values = form.getFieldsValue();
		setIsLoading(true);
		try {
			if (activeTabPage === "rumah123") {
				await handleGetRumah123Agent(values.email);
			}
			if (activeTabPage === "lamudi") {
				await handleGetLamudiAgent(values.email);
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<React.Fragment>
			<Space
				direction="vertical"
				size="small"
			>
				<HeaderSection
					title="Portal Checker"
					tabsComponent={
						<Tabs
							renderTabBar={(props, TabNavList) => (
								<TabNavList
									{...props}
									mobile={true}
								/>
							)}
							activeKey={activeTabPage}
							onChange={(v) => {
								setActiveTabPage(v);
								setIsChecker(false);
							}}
							items={[
								{
									key: "rumah123",
									label: "Rumah123",
								},
								{
									key: "lamudi",
									label: "Lamudi",
								},
							]}
						/>
					}
				></HeaderSection>

				<Card
					bordered={false}
					size="small"
				>
					<Space direction="vertical">
						<Form
							form={form}
							layout="vertical"
							name="basic"
							onFinish={handleChecker}
							autoComplete="off"
							style={{
								width: "50%",
							}}
						>
							<Form.Item
								name="email"
								label={activeTabPage === "rumah123" ? "Email Rumah123" : "Email Lamudi"}
								rules={generateFormRules("Email", ["required", "email"])}
							>
								<Input
									type="email"
									prefix={<MailOutlined style={{ color: "#A5B2BD" }} />}
									placeholder="Enter your email"
									onChange={() => setIsChecker(false)}
								/>
							</Form.Item>

							<Form.Item>
								<Button
									loading={isLoading}
									type="primary"
									htmlType="submit"
								>
									Check
								</Button>
							</Form.Item>
						</Form>

						{email && emailWatch && isChecker && (
							<Tabs
								renderTabBar={(props, TabNavList) => (
									<TabNavList
										{...props}
										mobile={true}
									/>
								)}
								activeKey={activeTab}
								onChange={(newTab) => {
									setActiveTab(newTab);
								}}
								onTabClick={(key) => {
									setActiveTab(key);
								}}
								items={
									[
										{
											key: "listing",
											label: (
												<Space>
													<span>Listing</span>
													<Badge count={listingFetch.pagination.totalData} />
												</Space>
											),
											children: (
												<Listing
													data={listingFetch.data}
													setQuery={listingFetch.setQuery}
													query={listingFetch.query}
													search={listingFetch.search}
													setSearch={listingFetch.setSearch}
													isLoading={listingFetch.isLoading}
													pagination={listingFetch.pagination}
													changePage={listingFetch.changePage}
												/>
											),
										},

										{
											key: "agent",
											label: (
												<Space>
													<span>Agent</span>
													<Badge count={agentFetch.pagination.totalData} />
												</Space>
											),
											children: (
												<>
													<Agent
														data={agentFetch.data}
														setQuery={agentFetch.setQuery}
														query={agentFetch.query}
														search={agentFetch.search}
														setSearch={agentFetch.setSearch}
														isLoading={agentFetch.isLoading}
														pagination={agentFetch.pagination}
														changePage={agentFetch.changePage}
													/>
												</>
											),
										},
									].filter(Boolean) as TabsProps["items"]
								}
							/>
						)}
					</Space>
				</Card>
			</Space>
		</React.Fragment>
	);
};
export default PortalChecker;
